/**
 * What a reaction pill looks like, and the pill itself.
 *
 * Its own module rather than a part of the reaction row, because the row, the expander and
 * the add button all wear this shape, and so does every button in the row's company.
 */

import { cn } from "cn";

import { BW_EMOJI_SRC } from "@/components/ui/editor/core/utils/bw-emoji";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Typography } from "@/components/ui/typography";
import type { ChannelMessageReaction } from "../_api/channel-endpoints";
import { BW_LOGO_REACTION } from "../_libs/message-reaction";

/**
 * Figma lays each pill's tint over an opaque base, so the 4px band where the row overlaps
 * the bubble reads as one solid shape rather than two translucent ones stacked. The tint is
 * the bubble's own token, applied as a background IMAGE so it can sit on top of the
 * `bg-bg-light` colour.
 */
const PILL_TINT = {
  own: "bg-[image:linear-gradient(var(--bg-primary-min),var(--bg-primary-min))]",
  other: "bg-[image:linear-gradient(var(--bg-dark-min),var(--bg-dark-min))]",
} as const;

/**
 * The same wash one step deeper, for a pill the pointer is on. It has to swap the IMAGE
 * rather than the background colour, because the colour is the opaque base the tint sits
 * on — and the house hover token for each wash is the one the buttons elsewhere use.
 */
const PILL_TINT_HOVER = {
  own: "hover:bg-[image:linear-gradient(var(--bg-primary-min-hover),var(--bg-primary-min-hover))]",
  other:
    "hover:bg-[image:linear-gradient(var(--bg-dark-min-hover),var(--bg-dark-min-hover))]",
} as const;

/**
 * The solid primary border a pill wears when the viewer is in it.
 *
 * The tint alone cannot carry it: on an own message (`align === "end"`) the pill is
 * ALREADY washed in `--bg-primary-min`, so a reacted pill there would differ from an
 * unreacted one by nothing but a count digit — which is what made a correct optimistic
 * update read as a dead button. The border separates them on both sides, and the incoming
 * side swaps its grey wash for the purple one as well (see {@link tintOf}).
 *
 * Deliberately NOT in {@link PILL_MOTION}'s transition list: the tint is a background
 * IMAGE and cannot transition anyway, and a border that eases in over 100ms is 100ms of
 * "did that work?". Both land in the frame the click paints.
 */
const PILL_BORDER_REACTED = "border-border-primary-max";

/**
 * Which wash a pill wears: the one its bubble lends the cluster — the viewer's own side
 * is the tinted one — and the tinted one for any pill the viewer is personally in,
 * wherever it sits.
 */
function tintOf(align: "start" | "end", reacted = false): "own" | "other" {
  return reacted || align === "end" ? "own" : "other";
}

/**
 * The shape every pill in the row shares: 24px tall, round, on an OPAQUE base. The base is
 * what keeps the bubble's own text from reading through the fill each pill lays over it in
 * the overlap band — the same stack the kanban order card hovers with, an opaque layer
 * under a translucent one.
 *
 * 24px matches the action bar's buttons, so both halves of a bubble's chrome are the same
 * control height. It leaves 22px of content box inside the border, which the 20px line box
 * of `ReactionGlyph` still sits in — the capsule got shorter, the emoji did not.
 *
 * `shrink-0` because a shrinkable pill would squash and clip its emoji rather than wrapping
 * onto the next line, which is the whole behaviour the row is built around.
 *
 * Shape only — no `visibility`, no `display`, no fill. The cluster's hover chrome is
 * `invisible` until hover (`_libs/message-hover.ts`), and a `visible` in here would win
 * that conflict (the caller's class is merged last) and pin it open.
 */
const PILL_SHAPE = cn(
  /* sorted */
  "items-center",
  "shrink-0",
  "h-6",
  "rounded-full border border-border-light-max",
  // The base is pinned through hover and through an open picker: a `Button` of this kind
  // recolours its background in both states (`hover:bg-bg-dark-min` and
  // `data-[state=open]:bg-bg-dark-min`), and both of those colours are translucent, so the
  // message would read through the pill exactly when the pointer is on it.
  "bg-bg-light hover:bg-bg-light data-[state=open]:bg-bg-light",
);

/**
 * The transition every size cue in the cluster rides.
 *
 * `scale` is named because Tailwind v4 draws `scale-*` with the `scale` property rather
 * than `transform` — a list naming only `transform` animates nothing. `color` and
 * `box-shadow` carry over from the button's own base; the fill is a background IMAGE,
 * which does not transition, so it swaps at once while the pill eases.
 *
 * `motion-reduce:transition-none` drops the easing and keeps the states themselves: a
 * press dip and a hover grow are feedback, not decoration.
 */
const PILL_MOTION = cn(
  /* sorted */
  "transition-[color,box-shadow,scale] duration-100",
  "motion-reduce:transition-none",
);

/**
 * Grows under the pointer; the other half of a cluster button's hover feedback is
 * {@link PILL_TINT_HOVER}. Off for a pill that anchors a surface of its own.
 */
const PILL_POP = "hover:scale-110 focus:scale-100 focus-visible:scale-100";

/**
 * Dips under the press.
 *
 * The one cue that owes nothing to state: it lands on `:active`, in the frame of the
 * mousedown, before the toggle has computed anything or the cache has been written. A
 * toggle whose only answer is a count digit changing reads as a dead button no matter how
 * fast the update is, which is why every button pill wears this and not just the ones
 * that grow.
 */
const PILL_PRESS = "active:scale-95";

/**
 * A pill that belongs to the bubble: the bubble's own tint over {@link PILL_SHAPE}, and
 * the primary outline when the viewer is in it.
 */
export function pillClassName(
  align: "start" | "end",
  { reacted = false }: { reacted?: boolean } = {},
): string {
  return cn(
    PILL_SHAPE,
    PILL_TINT[tintOf(align, reacted)],
    // After `PILL_SHAPE`'s `border-border-light-max` and in the same conflict family, so
    // `cn` resolves it last-wins — the same mechanic the tint-over-opaque-base stack above
    // already relies on.
    reacted && PILL_BORDER_REACTED,
  );
}

/**
 * A pill that is a button: the bubble's tint, deepening under the pointer, dipping under
 * the press. The pills, the add button, the "+N" and every action wear it, so the cluster
 * answers a pointer the same way wherever it lands.
 *
 * `pop: false` drops the GROWTH only — the press dip stays — for a pill that is an anchor
 * as well as a button: the "+N" opens a surface positioned against its own box, and a
 * reaction pill carries a tooltip, so a trigger that swells under the pointer drags that
 * surface out from under itself.
 */
export function buttonPillClassName(
  align: "start" | "end",
  { pop = true, reacted = false }: { pop?: boolean; reacted?: boolean } = {},
): string {
  return cn(
    pillClassName(align, { reacted }),
    PILL_TINT_HOVER[tintOf(align, reacted)],
    PILL_MOTION,
    PILL_PRESS,
    pop && PILL_POP,
  );
}

type ReactionPillProps = {
  reaction: ChannelMessageReaction;
  isReacted: boolean;
  tooltip: string;
  align: "start" | "end";
  disabled: boolean;
  onToggle: () => void;
  /** The row's own layout state — whether this pill is one it is currently showing. */
  className?: string;
};

export function ReactionPill({
  reaction,
  isReacted,
  tooltip,
  align,
  disabled,
  onToggle,
  className,
}: ReactionPillProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          data-test="message-reaction"
          data-reaction={reaction.type}
          data-reacted={isReacted || undefined}
          aria-pressed={isReacted}
          disabled={disabled}
          onClick={onToggle}
          className={cn(
            /* sorted */
            "flex",
            "gap-1",
            "px-2",
            "cursor-pointer disabled:cursor-default",
            // A button pill, not a bare one: it is the cluster's primary control, so it
            // owes a press dip and the hover deepening every other button here has.
            // `pop: false` because it anchors its own tooltip, and a trigger that swells
            // under the pointer drags that tooltip out from under itself.
            //
            // In a closed channel the pill is read-only (the server answers 409), so it
            // goes back to the bare shape: no hover wash, no press dip — nothing that
            // offers a click the channel will not take. It still wears the reacted
            // outline, because what the viewer already reacted with is still true.
            disabled
              ? pillClassName(align, { reacted: isReacted })
              : buttonPillClassName(align, { pop: false, reacted: isReacted }),
            className,
          )}
        >
          <ReactionGlyph type={reaction.type} />
          <Typography size="xs" className="text-text-dark-max">
            {reaction.count}
          </Typography>
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        <Typography size="xs" className="text-text-light">
          {tooltip}
        </Typography>
      </TooltipContent>
    </Tooltip>
  );
}

/** The emoji itself: the character, or the Birdwatch logo image for its shortcode. */
export function ReactionGlyph({ type }: { type: string }) {
  if (type === BW_LOGO_REACTION) {
    return <img src={BW_EMOJI_SRC} alt="Birdwatch" className="size-4" />;
  }
  // Figma's 14px glyph in a 20px line box. The capsule's height is fixed at 24px and the
  // row centres this in it, so the line box sets the glyph's rhythm and nothing else.
  return <span className="text-sm leading-5">{type}</span>;
}
